import { Collapse, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  updatePieChartStyleOptions,
  updateDoughChartStyleOptions,
  updateRoseChartStyleOptions,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { ColorSchemes } from './ColorScheme';
import ChartColors from './ChartColors';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import CustomColorBuilder from './CustomColorBuilder';
import { RootState } from '../../../redux';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';
import { limitChartData } from '../../CommonFunctions/CommonFunctions';

const CircularColorControls = ({ iconColor }: { iconColor?: string }) => {
  const { chartControls, tabTileProps, chartProperties } = useSelector((state: RootState) => {
    return {
      chartControls: state.chartControls,
      tabTileProps: state.tabTileProps,
      chartProperties: state.chartProperties,
    };
  });
  const dispatch = useDispatch();
  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  const chartType = chartProperties.properties[propKey].chartType;

  let currentControls: any = {};
  let updateAction: Function | null = null;

  if (chartType === 'pie') {
    currentControls = chartControls.properties[propKey].pieChartControls || {};
    updateAction = updatePieChartStyleOptions;
  } else if (chartType === 'donut' || chartType === 'doughnut') {
    currentControls = chartControls.properties[propKey].doughnutChartControls || {};
    updateAction = updateDoughChartStyleOptions;
  } else if (chartType === 'rose') {
    currentControls = chartControls.properties[propKey].roseChartControls || {};
    updateAction = updateRoseChartStyleOptions;
  }

  const updateStyleOptions = (propKey: string, option: string, value: any) => {
    if (updateAction) {
      dispatch(updateAction(propKey, option, value));
    }
  };

  const chartData = chartControls.properties[propKey].chartData || [];
  const allThemes = useCombinedColorSchemes();

  const currentSchemeName = chartControls.properties[propKey].colorScheme;
  const selectedScheme = allThemes.find((scheme) => scheme.name === currentSchemeName);
  const colorSchemes = selectedScheme?.colors || ColorSchemes[0].colors;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [selectedColor, setSelectedColor] = useState('');
  const [selectedNodeValueKey, setSelectedNodeValueKey] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const lastManualSchemeRef = useRef(currentSchemeName);

  useEffect(() => {
    if (currentControls.manualColor) {
      lastManualSchemeRef.current = currentSchemeName;
    }
  }, [currentControls.manualColor, currentSchemeName]);

  const dimensionField = chartProperties.properties[propKey].chartAxes[1]?.fields[0];
  const currentDimName = dimensionField
    ? dimensionField.displayname || dimensionField.fieldname
    : '';
  const prevDimRef = useRef(currentDimName);

  useEffect(() => {
    if (prevDimRef.current && prevDimRef.current !== currentDimName) {
      updateStyleOptions(propKey, 'nodeValuesColors', {});
    }
    prevDimRef.current = currentDimName;
  }, [currentDimName, propKey, dispatch, updateAction]);

  const globalAllColor = currentControls.allSeriesColor || '';
  const manualNodeColors = currentControls.nodeValuesColors || {};
  const hasAnyManualChanges = !!globalAllColor || Object.keys(manualNodeColors).length > 0;
  const measureField = chartProperties.properties[propKey].chartAxes[2]?.fields[0];
  const measureName = measureField ? measureField.displayname : '';
  const limitedChartData = limitChartData(chartData, 50, measureName);

  let allProcessedValues: string[] = [];
  if (currentDimName && Array.isArray(limitedChartData)) {
    const rawValues = limitedChartData.map((d: any) => {
      const lookupKey = dimensionField.displayname || dimensionField.fieldname;
      const val = d[lookupKey];

      return val === null || val === 'null' ? '(Blank)' : String(val);
    });
    allProcessedValues = Array.from(new Set(rawValues));
  }

  const uniqueDataValues = allProcessedValues.filter((val) => val !== '(Blank)');
  const manualKeys = Object.keys(currentControls.nodeValuesColors || {});

  const customValues = manualKeys.filter(
    (key) => !allProcessedValues.includes(key) && key !== '(Blank)' && key !== 'null',
  );

  const handleAddCustomColor = (name: string, color: string) => {
    const currentMap = currentControls.nodeValuesColors || {};
    const newMap = { ...currentMap, [name]: color };
    updateStyleOptions(propKey, 'nodeValuesColors', newMap);
  };

  const handleRemoveColorRule = (name: string) => {
    const currentMap = { ...(currentControls.nodeValuesColors || {}) };
    delete currentMap[name];
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const handleRemoveAllCustomColors = () => {
    const currentMap = { ...(currentControls.nodeValuesColors || {}) };
    customValues.forEach((key) => {
      delete currentMap[key];
    });
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const handleFullReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateStyleOptions(propKey, 'allSeriesColor', '');
    updateStyleOptions(propKey, 'nodeValuesColors', {});
  };

  const handleColorChange = (color: string) => {
    if (selectedNodeValueKey === 'ALL_SERIES_COLOR') {
      updateStyleOptions(propKey, 'allSeriesColor', color);
      updateStyleOptions(propKey, 'nodeValuesColors', {});
    } else {
      const currentMap = currentControls.nodeValuesColors || {};
      const newMap = { ...currentMap, [selectedNodeValueKey]: color };
      updateStyleOptions(propKey, 'nodeValuesColors', newMap);
    }
  };

  const handleOpenColorPicker = (
    event: React.MouseEvent<HTMLElement>,
    color: string,
    key: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedColor(color);
    setSelectedNodeValueKey(key);
  };

  const renderRow = (val: string, isCustomList: boolean) => {
    const valStr = String(val);
    const specificColorMap = currentControls.nodeValuesColors || {};
    const dataIndex = allProcessedValues.indexOf(valStr);

    let defaultBaseColor = '#cccccc';

    if (dataIndex >= 0) {
      if (globalAllColor) {
        defaultBaseColor = globalAllColor;
      } else {
        defaultBaseColor = colorSchemes[dataIndex % colorSchemes.length];
      }
    }

    const displayColor = specificColorMap[valStr] || defaultBaseColor;
    const isFutureValue = isCustomList;
    const labelText = valStr === 'null' ? '(Blank)' : valStr;

    return (
      <div
        key={valStr}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 8,
          width: '100%',
        }}
      >
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '10px',
            borderRadius: '2px',
          }}
          onClick={(e) => handleOpenColorPicker(e, displayColor, valStr)}
        />

        <label
          style={{
            fontSize: '12px',
            marginLeft: '5px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isFutureValue ? '#2bb9bb' : iconColor || 'inherit',
            cursor: 'default',
            flexGrow: 1,
            textAlign: 'left',
          }}
          title={isFutureValue ? `${labelText} (Custom Value)` : labelText}
        >
          {labelText}
        </label>
      </div>
    );
  };

  const renderAllControl = () => {
    const displayColor = globalAllColor || '#ffffff';
    const showReset = hasAnyManualChanges;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 8,
          marginBottom: 12,
          width: '100%',
        }}
      >
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '5px',
            borderRadius: '2px',
            backgroundImage: !globalAllColor
              ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
              : 'none',
            backgroundSize: !globalAllColor ? '10px 10px' : 'auto',
            backgroundPosition: !globalAllColor ? '0 0, 0 5px, 5px -5px, -5px 0px' : '0 0',
          }}
          onClick={(e) => handleOpenColorPicker(e, globalAllColor || '#000000', 'ALL_SERIES_COLOR')}
        />

        <label
          style={{
            fontSize: '13px',
            fontWeight: 600,
            marginLeft: '10px',
            flexGrow: 1,
            cursor: 'default',
            textAlign: 'left',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          All
        </label>

        {showReset && (
          <IconButton
            size="small"
            onClick={handleFullReset}
            sx={{
              padding: '2px',
              marginLeft: '5px',
              color: iconColor || 'lightgray',
              '&:hover': {
                color: iconColor || 'gray',
                backgroundColor: 'transparent',
              },
            }}
            title="Reset All Manual Changes"
          >
            <RestartAltIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </div>
    );
  };

  const renderColorControls = () => {
    if (!currentDimName) return null;

    return (
      <div
        style={{
          marginBottom: '10px',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '5px',
        }}
      >
        <div
          className="optionDescription"
          style={{ marginTop: '0', display: 'flex', justifyContent: 'space-around' }}
        >
          <label
            style={{
              width: '100%',
              fontWeight: 600,
              fontSize: '13px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            {dimensionField.displayname || currentDimName}
          </label>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ padding: 2 }}
            title={isExpanded ? 'Collapse List' : 'Expand List'}
          >
            {isExpanded ? (
              <KeyboardArrowUpIcon
                fontSize="small"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            ) : (
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            )}
          </IconButton>
        </div>

        <Collapse in={isExpanded}>
          <div
            style={{
              marginTop: '5px',
              paddingLeft: '10px',
              paddingBottom: '10px',
            }}
          >
            {renderAllControl()}
            {uniqueDataValues.map((val) => renderRow(val, false))}
          </div>
        </Collapse>

        <div
          style={{
            marginTop: '15px',
            padding: '10px 10px 0px',
            borderTop: '1px dashed #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography variant="caption" style={{ color: iconColor || '#888', fontWeight: 600 }}>
            Default for Blank / Empty
          </Typography>
          {renderRow('(Blank)', false)}
        </div>

        <CustomColorBuilder
          onAdd={handleAddCustomColor}
          existingColors={currentControls.nodeValuesColors || {}}
          onRemove={handleRemoveColorRule}
          onRemoveAll={handleRemoveAllCustomColors}
          customList={customValues}
          colorMap={currentControls.nodeValuesColors || {}}
          colorPalette={colorSchemes}
          onColorChange={handleAddCustomColor}
          iconColor={iconColor}
        />
      </div>
    );
  };

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px' }}>
      <OrientationRadioGroup
        orientation={currentControls.manualColor ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          const isSwitchingToManual = key === 'manual';

          if (isSwitchingToManual) {
            if (currentSchemeName !== lastManualSchemeRef.current) {
              updateStyleOptions(propKey, 'allSeriesColor', '');
              updateStyleOptions(propKey, 'nodeValuesColors', {});
            }
            updateStyleOptions(propKey, 'manualColor', true);
          } else {
            updateStyleOptions(propKey, 'manualColor', false);
          }
        }}
        style={{ width: '94%' }}
      />

      <div>
        {currentControls.manualColor ? (
          <>
            <div style={{ marginBottom: '10px', paddingRight: '10px' }}>
              {renderColorControls()}
            </div>

            <ColorPickerPopover
              isOpen={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              currentColor={selectedColor}
              onColorChange={handleColorChange}
              propKey={propKey}
              selectedMenu={''}
            />
          </>
        ) : (
          <div style={{ marginTop: '-15px' }}>
            <ChartColors iconColor={iconColor} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CircularColorControls;
